import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

type AlertCloseTimerButtonProps<T> = {
  onClick: (cx: T) => void;
  delay?: number; // in seconds
  cx: T;
};

export const AlertCloseTimerButton = <T,>({ onClick, delay = 0, cx }: AlertCloseTimerButtonProps<T>) => {
  const [progress, setProgress] = useState(0);
  const startTimeRef = useRef<number>(Date.now());
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!delay || delay <= 0) return;
    timerRef.current = window.setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const percentage = Math.min(100, (elapsed / delay) * 100);
      setProgress(percentage);
    }, 100);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [delay]);

  return (
    <Box sx={{ position: 'relative', width: 28, height: 28 }}>
      {delay > 0 && (
        <CircularProgress
          variant="determinate"
          value={progress}
          size={28}
          thickness={5}
          sx={{
            color: 'rgba(0, 0, 0, 0.1)',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        />
      )}
      <Button
        onClick={() => onClick(cx)}
        variant="outlined"
        size="small"
        sx={{
          minWidth: 0,
          p: 0.5,
          borderRadius: '50%',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1,
          border: 'none',
          color: 'inherit',
        }}
      >
        <CloseIcon fontSize="small" />
      </Button>
    </Box>
  );
};
